import { Alert, Modal, Steps, Typography } from "antd";
import { Formik } from "formik";
import { cloneDeep, get, isEmpty, set } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import Button from "shared/components/Button";
import Radio from "shared/components/Form/Radio";
import Input from "shared/components/Input";
import { NAME_SPACES } from "shared/locales/constants";
import { GENDER, ORGANIZATION_DUPLICABLE_ENTITIES } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import validation from "./validation";

const { Paragraph, Text } = Typography;

const STEP_FIELDS = [
  ["name"],
  [],
  ["email", "user.gender", "user.firstname", "user.lastname", "user.phone"],
];

const DuplicateModal = ({ open, onClose, onSubmit, organization, loading }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.BROKER);
  const DUPLICATE = t("DUPLICATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (open) setCurrentStep(0);
  }, [open]);

  const steps = [
    { title: DUPLICATE.STEPS.ORGANIZATION },
    { title: DUPLICATE.STEPS.ENTITIES },
    { title: DUPLICATE.STEPS.LEGAL },
  ];
  const isLastStep = currentStep === steps.length - 1;

  const revealStepErrors = (step, { setTouched, touched }) =>
    setTouched(
      STEP_FIELDS[step].reduce(
        (accumulator, field) => set(accumulator, field, true),
        cloneDeep(touched),
      ),
    );

  const stepIsInvalid = (step, errors) =>
    !isEmpty(STEP_FIELDS[step].filter((field) => get(errors, field)));

  const goNext = async (formikProps) => {
    const errors = await formikProps.validateForm();
    if (stepIsInvalid(currentStep, errors)) {
      revealStepErrors(currentStep, formikProps);
      return;
    }
    setCurrentStep((step) => step + 1);
  };

  const onFinish = async (formikProps) => {
    const errors = await formikProps.validateForm();
    const firstInvalid = STEP_FIELDS.findIndex((_, step) =>
      stepIsInvalid(step, errors),
    );
    if (firstInvalid !== -1 && firstInvalid !== currentStep) {
      revealStepErrors(firstInvalid, formikProps);
      setCurrentStep(firstInvalid);
      return;
    }
    formikProps.handleSubmit();
  };

  const renderOrganizationStep = (formikProps) => (
    <>
      <Paragraph type="secondary">
        {DUPLICATE.DUPLICATING}{" "}
        <Text strong>{get(organization, "name", "-")}</Text>
      </Paragraph>

      <div className="m-bottom-16">
        <Input
          label={DUPLICATE.NAME}
          placeholder={DUPLICATE.NAME}
          required
          {...bindInputProps({ name: "name", ...formikProps })}
        />
      </div>

      <Alert
        type="info"
        showIcon
        className="m-bottom-16"
        message={DUPLICATE.IDENTIFIERS_NOTE}
      />
    </>
  );

  const renderEntitiesStep = (formikProps) => (
    <>
      <div className="m-bottom-16">
        <Text strong>{DUPLICATE.ENTITIES}</Text>
        {ORGANIZATION_DUPLICABLE_ENTITIES.map((key) => (
          <Input
            key={key}
            type="checkbox"
            name={`entities.${key}`}
            label={get(DUPLICATE, `ENTITY.${key}`)}
            checked={get(formikProps, `values.entities.${key}`, false)}
            onChange={({ target: { checked } }) =>
              formikProps.setFieldValue(`entities.${key}`, checked)
            }
          />
        ))}
      </div>

      <Alert
        type="info"
        showIcon
        className="m-bottom-16"
        message={DUPLICATE.INFO}
      />
    </>
  );

  const renderLegalStep = (formikProps) => (
    <div className="grid--1">
      <Text strong>{DUPLICATE.ADMIN_ACCOUNT}</Text>
      <div>
        <Input
          type="email"
          label={DUPLICATE.ADMIN_EMAIL}
          placeholder={DUPLICATE.ADMIN_EMAIL_PLACEHOLDER}
          required
          {...bindInputProps({ name: "email", ...formikProps })}
        />
        <Text type="secondary">{DUPLICATE.ADMIN_EMAIL_HELP}</Text>
      </div>
      <Radio
        isHorizontal={false}
        label={FORM.CIVILITY.LABEL}
        options={[
          { label: FORM.CIVILITY.MALE, value: GENDER.MALE },
          { label: FORM.CIVILITY.FEMALE, value: GENDER.FEMALE },
        ]}
        required
        {...bindInputProps({ name: "user.gender", ...formikProps })}
      />
      <Input
        label={FORM.FIRST_NAME}
        placeholder={FORM.FIRST_NAME}
        required
        {...bindInputProps({ name: "user.firstname", ...formikProps })}
      />
      <Input
        label={FORM.LAST_NAME}
        placeholder={FORM.LAST_NAME}
        required
        {...bindInputProps({ name: "user.lastname", ...formikProps })}
      />
      <Input
        label={FORM.PHONE}
        placeholder={FORM.PHONE}
        required
        {...bindInputProps({ name: "user.phone", ...formikProps })}
      />
    </div>
  );

  const renderStep = (formikProps) =>
    [renderOrganizationStep, renderEntitiesStep, renderLegalStep][currentStep](
      formikProps,
    );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      title={DUPLICATE.TITLE}
      width={600}
    >
      <Formik
        initialValues={{
          name: `${get(organization, "name", "")} ${DUPLICATE.COPY_SUFFIX}`,
          email: "",
          user: {
            gender: null,
            firstname: null,
            lastname: null,
            phone: null,
          },
          entities: ORGANIZATION_DUPLICABLE_ENTITIES.reduce(
            (accumulator, name) => ({ ...accumulator, [name]: true }),
            {},
          ),
        }}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={validation(
          t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        )}
      >
        {(formikProps) => (
          <>
            <Steps
              size="small"
              current={currentStep}
              items={steps}
              className="m-bottom-24"
              onChange={(step) => {
                if (step < currentStep) setCurrentStep(step);
              }}
            />

            {renderStep(formikProps)}

            <div className="d-flex justify--between m-top-24">
              <Button
                type="primary--outlined"
                disabled={currentStep === 0}
                onClick={() => setCurrentStep((step) => step - 1)}
                icon={<BiChevronLeft size={18} />}
              >
                {DUPLICATE.PREVIOUS}
              </Button>
              {isLastStep ? (
                <Button
                  type="primary"
                  loading={loading}
                  onClick={() => onFinish(formikProps)}
                >
                  {DUPLICATE.SUBMIT}
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={() => goNext(formikProps)}
                  icon={<BiChevronRight size={18} />}
                >
                  {DUPLICATE.NEXT}
                </Button>
              )}
            </div>
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default DuplicateModal;
